import Clipboard from '@react-native-clipboard/clipboard';
import React, { FC } from 'react';
import styled from 'styled-components/native';

import { IDemoAccountResponse, IRegisterDemoAccountResponse } from '@webtrader/core/api/experienceDemo';
import dynamicSize from '@webtrader/core/dynamic-size';
import { isRTL, useTranslation } from '@webtrader/core/i18n';
import { useTheme } from '@webtrader/core/services/theme/v2';
import { checkIsNative } from '@webtrader/core/validators/device';

import { AlertManager } from '@/components/Alert/Manager';
import IonicIcons from '@/components/Icons/Ionic';
import Text from '@/components/Text';
import { openInAppBrowser } from '@/utils/web';

interface IExperienceDemoCompletionProps {
  title?: string | undefined;
  subTitle?: string | undefined;
  accountDetails: IRegisterDemoAccountResponse | IDemoAccountResponse | null | undefined;
}

const ExperienceDemoCompletionContent: FC<IExperienceDemoCompletionProps> = (props) => {
  const { title, subTitle, accountDetails } = props;

  const { serverUrl, login, password } = accountDetails || {};

  const { t } = useTranslation();

  const { theme } = useTheme();

  const onCopy = () => {
    let textToCopy = '';

    if (serverUrl) {
      textToCopy += `ServerUrl: ${serverUrl}\n`;
    }

    if (login) {
      textToCopy += `Login: ${login}\n`;
    }
    if (password) {
      textToCopy += `Password: ${password}\n`;
    }

    if (checkIsNative()) {
      Clipboard.setString(textToCopy);
    } else {
      navigator.clipboard.writeText(textToCopy);
    }
    AlertManager().alert({
      message: t('Copied to Clipboard')
    });
  };

  const gotToServerUrl = () => {
    openInAppBrowser(serverUrl);
  };

  return (
    <>
      <StyledCenter>
        <IonicIcons name="checkmark-circle-outline" size={60} color={theme.colors.success[0]} />
        <StyledTitleWrapper>
          <StyledTitle>{title}</StyledTitle>
          <StyledTitle>{subTitle}</StyledTitle>
        </StyledTitleWrapper>
        <StyledText>{t('Here are the login and account details.')}</StyledText>
        <StyledText>{t('We’ve also sent these to your email.')}</StyledText>
      </StyledCenter>
      <StyledHeader>
        <StyledText textAlign={isRTL() ? 'right' : 'left'} bold>
          {t('Account details')}
        </StyledText>
        <StyledIconButton onPress={onCopy}>
          <StyledCopyIcon>
            <IonicIcons name="copy-outline" size={16} color={theme.colors.text[1]} />
          </StyledCopyIcon>
          <StyledText textAlign={isRTL() ? 'right' : 'left'} bold>
            {t('Copy')}
          </StyledText>
        </StyledIconButton>
      </StyledHeader>
      {!!serverUrl && (
        <StyledListItem>
          <StyledItemTitle>{t('Server URL')}</StyledItemTitle>
          <StyledTextInfoLink onPress={gotToServerUrl}>{serverUrl || ''}</StyledTextInfoLink>
        </StyledListItem>
      )}
      <StyledListItem>
        <StyledItemTitle>{t('Login')}</StyledItemTitle>
        <StyledTextInfo>{login || ''}</StyledTextInfo>
      </StyledListItem>
      <StyledListItem>
        <StyledItemTitle>{t('Password')}</StyledItemTitle>
        <StyledTextInfo>{password || ''}</StyledTextInfo>
      </StyledListItem>
      <StyledSpace />
      <StyledCenter>
        <StyledText>{t('This account will expire in 30 days.')}</StyledText>
      </StyledCenter>
    </>
  );
};

export default ExperienceDemoCompletionContent;

const StyledCenter = styled.View`
  align-items: center;
`;

const StyledTitle = styled(Text)`
  font-size: ${dynamicSize.convertSize(16)}px;
  line-height: ${dynamicSize.convertSize(24)}px;
  font-weight: bold;
  padding: 0;
  margin: 0;
  text-align: center;
`;

const StyledTitleWrapper = styled.View`
  margin: ${dynamicSize.convertSize(12)}px 0 ${dynamicSize.convertSize(8)}px 0;
`;

type StyledTextProps = {
  color?: string;
  bold?: boolean;
  textAlign?: 'left' | 'center' | 'right';
};

const StyledText = styled(Text)<StyledTextProps>`
  line-height: ${dynamicSize.convertSize(18)}px;
  padding: 0;
  margin: 0;
  color: ${(props) => (props.color ? props.color : props.theme.colors.text[1])};
  font-weight: ${(props) => (props.bold ? '600' : '400')};
  letter-spacing: 0.5;
  line-height: ${dynamicSize.convertSize(21)}px;
  text-align: ${(props) => (props.textAlign ? props.textAlign : 'center')};
  flex-shrink: 1;
`;

const StyledTextInfo = styled(StyledText)`
  text-align: ${() => (checkIsNative() ? 'right' : 'end')};
  color: ${({ theme }) => theme.colors.text[0]};
`;

const StyledTextInfoLink = styled(StyledTextInfo)`
  text-align: ${() => (checkIsNative() ? 'right' : 'end')};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
`;

const StyledItemTitle = styled(StyledText)`
  min-width: 30%;
  text-align: ${() => (checkIsNative() ? 'left' : 'start')};
  margin-end: ${dynamicSize.convertSize(16)}px;
`;

const StyledHeader = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background[1]};
  border-radius: ${dynamicSize.convertSize(8)}px;
  padding: ${dynamicSize.convertSize(8)}px ${dynamicSize.convertSize(16)}px;
  margin-top: ${dynamicSize.convertSize(24)}px;
  margin-bottom: ${dynamicSize.convertSize(8)}px;
`;

const StyledListItem = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${dynamicSize.convertSize(4)}px ${dynamicSize.convertSize(16)}px;
`;

const StyledSpace = styled.View`
  height: ${dynamicSize.convertSize(24)}px;
`;

const StyledIconButton = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: ${dynamicSize.convertSize(4)}px;
`;

const StyledCopyIcon = styled.View`
  margin-end: ${dynamicSize.convertSize(4)}px;
`;
